import { DeleteButton } from '@/components/_common';
import { useCreateWorkOrderItemList } from '@/core/domain/workorder/WorkOrdersItems/WorkOrdersItems.hooks';
import { WorkOrderItemType } from '@/core/domain/workorder/WorkOrdersItems/WorkOrdersItems.types';
import {
  Button,
  Col,
  Divider,
  Dropdown,
  Form,
  Menu,
  message,
  Modal,
  Progress,
  Row,
  Space,
  Tooltip,
  Typography,
} from 'antd';
import { AxiosError } from 'axios';
import { useCallback, useEffect, useState } from 'react';
import { useWorkorderItems, WorkorderItem } from '..';
import { useWorkorderDetailContext } from '../../../WorkorderDetailProvider';
import WorkOrderItemHistoryButton from '../../WorkOrderItemHistoryButton';
import WorkOrderItemPartForm, {
  WorkOrderItemFormData,
} from '../../WorkOrderItemPartForm';
import WorkOrderItemServiceForm from '../../WorkOrderItemServiceForm';
import { PartListType } from '../WorkOrderSteps';
import ModalLoadingOverlay from '@/components/_common/ModalLoadingOverlay';
import { useWorkorderItemPermissions } from '../../WorkOrderItemPartForm/workorderItemPermissions';
import { isAppPortocar } from '@/index';
import { PortoApoliceType } from '@/core/domain/porto-seguro/apolices/apolices.types';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import { AuthorityWorkorder } from '@/types/Authority/Authority.Workorder';

type Props = {
  onCancel: VoidFunction;
  onPrev: VoidFunction;
  partListType: PartListType;
};

const WorkOrderSetupItems: React.FC<Props> = ({
  onCancel,
  onPrev,
  partListType,
}) => {
  const [form] = Form.useForm<WorkOrderItemFormData>();
  const [currentSetup, setCurrentSetup] = useState(0);
  const {
    items,
    setItems,
    itemType,
    hasNewItem,
    isLastSetup,
    setIsLastSetup,
    onPrevStep,
    kit,
  } = useWorkorderItems();

  const {
    complement: { data: complement },
    workorder: { data: workOrder },
  } = useWorkorderDetailContext();
  const percent = (100 * currentSetup + 1) / ((items?.length || 0) - 1);

  const createWorkorderItemList = useCreateWorkOrderItemList(workOrder?.id);

  const currentItem = items[currentSetup] as WorkorderItem | undefined;

  useEffect(() => {
    if (hasNewItem) {
      addNewItem();
    }
  }, [hasNewItem]);

  useEffect(() => {
    if (currentSetup === items.length - 1) {
      setIsLastSetup(true);
    } else {
      setIsLastSetup(false);
    }
  }, [currentSetup, items]);

  const updateForm = useCallback(() => {
    if (currentItem) {
      form.setFieldsValue({
        ...currentItem,
        type: currentItem.itemType,
        discount: currentItem?.discount || 0,
        isInternal: String(!!currentItem?.isInternal),
        isPaintedPart: String(!!currentItem?.isPaintedPart),
        unitCost: currentItem?.unitCost || 0,
        unitPrice: currentItem?.unitPrice || 0,
        quantity: currentItem?.quantity || 0,
        discountValue: Number(
          ((currentItem?.quantity || 0) *
            (currentItem?.unitPrice || 0) *
            (currentItem?.discount || 0)) /
            100,
        ),
      });
    }
  }, [currentItem]);

  useEffect(() => {
    updateForm();
  }, [updateForm]);

  function insertItem(
    arr: typeof items,
    index: number,
    newItem: typeof items[0],
    replace?: boolean,
  ) {
    return [
      ...arr.slice(0, replace ? -index : index),
      newItem,
      ...arr.slice(index),
    ];
  }

  const getMinDiscountPolicy = useCallback(
    (type?: WorkOrderItemType) => {
      if (
        isAppPortocar &&
        type === WorkOrderItemType.Service &&
        !workOrder?.partnerDiscount &&
        workOrder?.employeeDiscount
      )
        return 10;

      if (
        !isAppPortocar ||
        !complement?.policyNumber ||
        type !== WorkOrderItemType.Service ||
        !workOrder?.partnerDiscount
      )
        return undefined;

      const isPortoPolicy =
        !!complement?.policyNumber &&
        complement?.companyCode?.toUpperCase() === PortoApoliceType.PORTO;

      return isPortoPolicy ? 20 : 15;
    },
    [
      isAppPortocar,
      complement?.policyNumber,
      complement?.companyCode,
      workOrder?.partnerDiscount,
    ],
  );

  async function addNewItem(type?: WorkOrderItemType) {
    try {
      if (!type || !complement) return;

      await form.validateFields();

      setItems(
        insertItem(items, currentSetup + 1, {
          complementId: complement.id,
          itemId: 0,
          itemName: '',
          itemType: type,
          quantity: 1,
          unitPrice: 0,
          discount: getMinDiscountPolicy(type) || 0,
          isInternal: true,
        }),
      );

      form.resetFields();

      setCurrentSetup(currentSetup + 1);
    } catch {
      validateFieldsMessage();
    }
  }

  function prevSetup() {
    setCurrentSetup(currentSetup - 1);
  }

  const itemPermissions = useWorkorderItemPermissions();

  async function nextSetup() {
    try {
      const values = await form.validateFields();

      if (!itemPermissions.allowSellingBellowCostValue) {
        const subTotal = (values.quantity || 0) * (values.unitPrice || 0);

        const amount = subTotal - values.discountValue;

        if (amount < (values.unitCost || 0)) {
          message.destroy();
          return message.warn(
            'O preço de venda deve ser maior que o custo unitário',
          );
        }
      }

      form.resetFields();
      setCurrentSetup(currentSetup + 1);
    } catch {
      validateFieldsMessage();
    }
  }

  async function handleSubmit() {
    try {
      if (!complement) return;

      const values = await form.validateFields();

      if (!itemPermissions.allowSellingBellowCostValue) {
        const subTotal = (values.quantity || 0) * (values.unitPrice || 0);

        const amount = subTotal - values.discountValue;

        if (amount < (values.unitCost || 0)) {
          message.destroy();
          return message.warn(
            'O preço de venda deve ser maior que o custo unitário',
          );
        }
      }

      await createWorkorderItemList.mutateAsync({
        complementId: complement.id,
        workOrderItems: items.map(({ configDiscount: _, ...item }) => ({
          ...item,
          complementId: complement.id,
          itemId: item.itemId === 0 ? undefined : item.itemId,
          isInternal: !!item.isInternal,
          quantity: item.quantity || 1,
          unitCost: item.unitCost || 0,
          discount: (item.discount || 0) / 100 || 0,
          isPaintedPart: item.isPaintedPart,
          kitId: kit?.id ?? item?.kitId,
          kitName: kit?.name ?? item?.kitName,
        })),
      });

      setItems([]);
      onCancel();
    } catch (error: unknown) {
      if (!(error as AxiosError).isAxiosError) {
        validateFieldsMessage();
      }
    }
  }

  function validateFieldsMessage() {
    message.destroy();
    message.warning('Preencha todos os campos!');
  }

  function onValuesChange(_: unknown, values: WorkOrderItemFormData) {
    setItems(
      items.map((item, index) =>
        index === currentSetup
          ? {
              ...item,
              ...values,
              isInternal: JSON.parse(values.isInternal),
              isPaintedPart: values.isPaintedPart
                ? JSON.parse(values.isPaintedPart)
                : undefined,
            }
          : item,
      ),
    );
  }

  function removeItem() {
    setItems(items.filter((_, index) => index !== currentSetup));

    if (items.length <= 1) {
      onPrevStep();
    }
    if (currentSetup === items.length - 1) {
      prevSetup();
    }
    updateForm();
  }

  const getItemName = () => {
    if (currentItem?.itemId) {
      return currentItem?.itemName;
    }
    return currentItem?.itemType === WorkOrderItemType.Part
      ? 'Digitada'
      : 'Digitado';
  };

  const getItemTypeLabel = () => {
    if (currentItem?.itemType === WorkOrderItemType.Part) {
      return 'Peça: ';
    }
    return 'Serviço: ';
  };

  function handlePrev() {
    if (partListType === PartListType.PARTS_FRAGA) {
      Modal.confirm({
        title: 'Atenção',
        content: (
          <>
            <p>
              Você está prestes a voltar para a listagem de seleção de peças.
            </p>
            <p>
              As peças cadastradas do catalogo Fraga, terão que ser selecionadas
              na lista de peças do sistema.
            </p>
          </>
        ),
        onOk: () => () => {
          onPrev();
          onPrevStep(1);
        },
        okText: 'Voltar a listagem',
      });
    } else {
      onPrevStep();
    }
  }

  return (
    <>
      {createWorkorderItemList.isLoading && (
        <ModalLoadingOverlay
          messages={['Salvando itens no orçamento...']}
          style={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            borderRadius: 8,
          }}
        />
      )}

      <Row align="middle" gutter={[16, 8]} style={{ marginTop: 24 }}>
        <Col flex={1}>
          <Progress
            percent={percent}
            format={() => <>{`${currentSetup + 1}/${items?.length}`}</>}
            style={{ paddingRight: 20 }}
          />
        </Col>

        <Col>
          <Space>
            <Button onClick={prevSetup} disabled={currentSetup < 1}>
              Anterior
            </Button>
            <Button
              onClick={nextSetup}
              disabled={currentSetup === (items?.length || 1) - 1}
            >
              Próximo
            </Button>
          </Space>
        </Col>
      </Row>

      <Divider />

      <Row
        gutter={[16, 8]}
        align="middle"
        justify="space-between"
        style={{ marginBottom: 24 }}
        wrap={false}
      >
        <Col flex={1}>
          <Typography.Title
            level={4}
            style={{ margin: 0, width: '100%' }}
            ellipsis
          >
            {getItemTypeLabel()}
            {getItemName()}
          </Typography.Title>
        </Col>

        <Col>
          <Tooltip
            title={
              !!currentItem?.kitId
                ? 'Não é possível remover item de kit'
                : undefined
            }
          >
            <div>
              <DeleteButton
                onClick={removeItem}
                disabled={!!currentItem?.kitId}
              >
                Remover
              </DeleteButton>
            </div>
          </Tooltip>
        </Col>
      </Row>

      {!!currentItem && (
        <>
          {currentItem?.itemType === WorkOrderItemType.Part && (
            <WorkOrderItemPartForm
              form={form}
              onValuesChange={onValuesChange}
              currentStep={currentSetup}
            />
          )}

          {currentItem?.itemType === WorkOrderItemType.Service && (
            <WorkOrderItemServiceForm
              form={form}
              onValuesChange={onValuesChange}
            />
          )}
        </>
      )}

      <Row gutter={[16, 8]} justify="space-between" style={{ marginTop: 24 }}>
        <Col>
          <Space>
            {itemType !== WorkOrderItemType.Kit &&
              hasPermissions([
                AuthorityWorkorder.MOVIMENTATION_WORKORDER_ADD_PART_SERVICE_TYPED,
              ]) && (
                <Button type="primary" onClick={() => addNewItem(itemType)}>
                  {itemType === WorkOrderItemType.Part
                    ? 'Nova peça digitada'
                    : 'Novo serviço digitado'}
                </Button>
              )}
            {itemType === WorkOrderItemType.Kit && (
              <Dropdown
                trigger={['click']}
                overlay={
                  <Menu
                    items={[
                      {
                        label: 'Nova peça digitada',
                        key: WorkOrderItemType.Part,
                        onClick: () => addNewItem(WorkOrderItemType.Part),
                      },
                      {
                        label: 'Novo serviço digitado',
                        key: WorkOrderItemType.Service,
                        onClick: () => addNewItem(WorkOrderItemType.Service),
                      },
                    ]}
                  />
                }
              >
                <Button type="primary">Adicionar item</Button>
              </Dropdown>
            )}

            {currentItem?.itemType === WorkOrderItemType.Part && (
              <WorkOrderItemHistoryButton itemPart={currentItem} />
            )}
          </Space>
        </Col>

        <Col>
          <Space>
            <Button onClick={onCancel}>Cancelar</Button>

            <Button onClick={handlePrev}>Voltar</Button>

            <Button
              type="primary"
              onClick={handleSubmit}
              disabled={!isLastSetup}
              loading={createWorkorderItemList.isLoading}
            >
              Salvar
            </Button>
          </Space>
        </Col>
      </Row>
    </>
  );
};

export default WorkOrderSetupItems;
